import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Virtuoso } from 'react-virtuoso';
import { faCircle } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCircleDot } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { theme } from '@components-shared/src/stitches';
import { NoResults } from '@components/basics/NoResults';
import { ReleaseCoverImagePlayButton } from '@components/basics/ReleaseCoverImagePlayButton';
import { Button } from '@components/main-components/button/Button';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import type { ArtistReleasesFilter, FragmentType } from '@graphql/reactQuery';
import { ArtistProfileNewDocument } from '@graphql/reactQuery';
import {
  ArtistMintedReleasesAuthorFilterOption,
  ArtistReleaseFragmentDoc,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { fetchGQL } from '@graphql/reactQuery';
import {
  ArtistReleasesDocument,
  getFragment,
  gql,
  SetArtistPickedReleaseDocument,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { ReleaseRowSkeleton } from './ReleaseRowSkeleton';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ArtistRelease on Release {
    id
    title
    createdAt
    artist {
      id
      name
      webappUri
    }
    staticCoverImage {
      id
      url
    }
    coverImage {
      id
      url
      dominantColor
    }
    track {
      id
      normalizedPeaks
      duration
    }
    type
    webappUri
    ...WaveformCommentsReleaseInfo
    ...ReleaseCoverImagePlayButton
  }

  query ArtistReleases(
    $artistId: UUID!
    $pagination: CursorConnectionArgs!
    $filter: ArtistReleasesFilter!
  ) {
    artist(id: $artistId) {
      id
      releases(pagination: $pagination, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...WaveformCommentsReleaseInfo
            ...ArtistRelease
          }
        }
      }
    }
  }

  mutation SetArtistPickedRelease($releaseId: UUID) {
    setArtistPickedRelease(releaseId: $releaseId) {
      __typename
      ... on Error {
        message
      }
      ... on MutationSetArtistPickedReleaseSuccess {
        data {
          id
        }
      }
    }
  }
`);

const INITIAL_PAGE_SIZE = 20;

const UserItemContent = (
  i: number,
  releaseFrag: FragmentType<ArtistReleaseFragmentDoc>,
  selectedReleaseId: string | null | undefined,
  setSelectedReleaseId: (id: string | null) => void,
) => {
  const release = getFragment(ArtistReleaseFragmentDoc, releaseFrag);
  const isSelected = selectedReleaseId === release.id;

  return (
    <div
      className="-mx-2 flex h-16 items-center justify-between rounded-lg px-2 hover:bg-base50"
      key={`EDIT_ARTIST_PICK_${i}`}
    >
      <div className="flex w-full items-center gap-3">
        <div className="h-12 w-12 flex-00auto">
          <ReleaseCoverImagePlayButton
            playButtonSize="sm"
            nextImageSizes="48px"
            releaseFrag={release}
          />
        </div>

        <div className="flex max-w-[175px] flex-col gap-1.5 md:max-w-[225px]">
          <AccessibleLink
            pathnameUri={webappUriPath(release.webappUri)}
            className="overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-xxs font-medium"
          >
            {release.title}
          </AccessibleLink>
          <AccessibleLink
            pathnameUri={webappUriPath(release.artist.webappUri)}
            className="font-base text-base-xs text-base500 "
          >
            {release.artist.name}
          </AccessibleLink>
        </div>
      </div>
      {isSelected ? (
        <IconButton
          icon={
            <FontAwesomeIcon icon={faCircleDot} size="sm" color={theme.colors.base800.toString()} />
          }
          onClick={() => {
            if (selectedReleaseId === release.id) {
              setSelectedReleaseId(null);
            } else {
              setSelectedReleaseId(release.id);
            }
          }}
        />
      ) : (
        <div className="transition-all duration-300 ease-in-out [&_path]:hover:brightness-[30%]">
          <IconButton
            icon={
              <FontAwesomeIcon icon={faCircle} size="sm" color={theme.colors.base500.toString()} />
            }
            onClick={() => setSelectedReleaseId(release.id)}
          />
        </div>
      )}
    </div>
  );
};

RefetchOnComplete({
  trigger: [SetArtistPickedReleaseDocument],
  refetch: [ArtistProfileNewDocument],
});

export const EditArtistPickModal = ({
  artistId,
  artistPickReleaseId,
}: {
  artistId: string;
  artistPickReleaseId: string | undefined;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();

  const [filter, setFilter] = useState<ArtistReleasesFilter>({
    releaseAuthor: ArtistMintedReleasesAuthorFilterOption.All,
  });

  const {
    orderedList: releases,
    loadMoreNextPage: loadMore,
    isLoadingNewPage,
  } = useInfiniteQuery(ArtistReleasesDocument, {
    filterQueryKey: {
      artistId,
      filter,
    },
    getNextPageParam({ data: { artist } }) {
      return artist?.releases.pageInfo.hasNextPage && { after: artist.releases.pageInfo.endCursor };
    },
    variables({ pageParam }) {
      return {
        artistId,
        pagination: {
          first: INITIAL_PAGE_SIZE,
          after: pageParam?.after || null,
        },
        sort: 'DESC',
        filter: {
          releaseAuthor: filter.releaseAuthor,
        },
      };
    },
    list({ artist }) {
      return artist?.releases.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null | undefined>(
    artistPickReleaseId,
  );

  const LoadingFooter = useCallback(
    () =>
      isLoadingNewPage ? (
        <>
          {Array.from({ length: 6 }, (_, i) => {
            return <ReleaseRowSkeleton key={i} />;
          })}
        </>
      ) : null,
    [isLoadingNewPage],
  );

  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const allReleasesFilter = ArtistMintedReleasesAuthorFilterOption.All;
  const authoredReleasesFilter = ArtistMintedReleasesAuthorFilterOption.OnlyAuthoredReleases;
  const appearsOnReleasesFilter = ArtistMintedReleasesAuthorFilterOption.OnlyAppearsOn;

  const body = (
    <div className="h-[532px]">
      <div className="flex gap-2">
        <Chip
          text="All"
          isSelected={filter.releaseAuthor === allReleasesFilter}
          onClick={() => setFilter({ releaseAuthor: allReleasesFilter })}
        />
        <Chip
          text="My releases"
          isSelected={filter.releaseAuthor === authoredReleasesFilter}
          onClick={() => setFilter({ releaseAuthor: authoredReleasesFilter })}
        />
        <Chip
          text="Appears on"
          isSelected={filter.releaseAuthor === appearsOnReleasesFilter}
          onClick={() => setFilter({ releaseAuthor: appearsOnReleasesFilter })}
        />
      </div>
      {releases.length === 0 && !isLoadingNewPage ? (
        <NoResults />
      ) : (
        <div className="mt-4">
          <Virtuoso
            data={releases}
            itemContent={(i, releaseFrag) =>
              UserItemContent(i, releaseFrag, selectedReleaseId, setSelectedReleaseId)
            }
            customScrollParent={scrollParent}
            endReached={loadMore}
            context={{ sourceLocation: 'Edit Artist Pick Modal' }}
            components={{ Footer: LoadingFooter }}
          />
        </div>
      )}
    </div>
  );

  return (
    <StandardModal
      ref={r => {
        setScrollParent(r || undefined);
      }}
      title="Edit Artist Pick"
      body={body}
      backCallToAction={
        <Button variant="secondary" label="Close" onClick={closeCancelModal} fullWidth />
      }
      callToAction={
        <Button
          variant="primary"
          label="Save"
          onClick={async () => {
            // TODO: Add try catch with error log
            await fetchGQL(SetArtistPickedReleaseDocument, {
              variables: {
                releaseId: selectedReleaseId,
              },
            });
            closeCancelModal();
          }}
          fullWidth
        />
      }
      closeModal={closeCancelModal}
    />
  );
};
